import { useCallback, useMemo, useRef, useState } from "react";
import { BLOCK_ICON_MAP } from "../utils";
import { CheckCircleIcon, CogIcon, ExclamationIcon, PlusCircleIcon, RefreshIcon, SearchIcon, XIcon } from "@heroicons/react/outline";
import { useModal } from "../contexts";
import { ChatGPTField, Document, SourceField, Workflow } from "../interfaces";
import { BadRequestError, OAuthError, postData } from "../backend";
import WorkflowSourceFieldTableRow from "./WorkflowSourceFieldTableRow";
import WorkflowChatGPTFieldTableRow from "./WorkflowChatGPTFieldTableRow";
import WorkflowChatGPTModal from "./WorkflowChatGPTModal";
import PortantAILabel from "./PortantAILabel";
import useUser from "../hooks/useUser";
import PayWallModalPortantAI from "./PayWallModalPortantAI";
import { KeyedMutator } from "swr";
import OAuthModal from "./OAuthModal";
import InaccessibleFilesModal from "./InaccessibleFilesModal";
import { useTranslation } from "react-i18next";
import SmallTextInputField from "./SmallTextInputField";
import WorkflowModalHubspotPropertiesList from "./WorkflowModalHubspotPropertiesList";

interface Props {
  workflow: Workflow;
  mutateWorkflow: KeyedMutator<Workflow>;
  document?: Document;
  showRefreshSpinning?: boolean; // Override isRefreshing **only visually** to show the refresh button as spinning
  hideChatGPTField?: boolean;
  indexChatGPTField?: number;
  disableCopy?: boolean;
}

function WorkflowSourceFieldTableHubSpot(props: Props) {
  const { user } = useUser();
  const { t } = useTranslation();
  const { openModal } = useModal();

  const { workflow, mutateWorkflow } = props;
  const source = workflow.source!

  const tableRef = useRef<HTMLDivElement>(null);
  const [sourceSearch, setSourceSearch] = useState({ show: false, value: '' });
  const [openField, setOpenField] = useState<string | null>(null);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [missingFields, setMissingFields] = useState<Array<string>>();

  const refresh = useCallback(async () => {
    setIsRefreshing(true);

    await postData(`/hubspot/config/${source.hubspotConfig?.id}/refresh/`)
      .then(hubspotConfig => mutateWorkflow({ ...workflow, source: { ...source, hubspotConfig } }, { revalidate: false }))
      .catch(console.error);

    try {
      await postData(`/workflows/${workflow.id}/check/`)
        .then(() => setMissingFields([])); // No error means no missing fields

    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} />)

      else if (error instanceof BadRequestError) {
        if (error.reason === "MISSING_FIELDS") {
          setMissingFields(error.payload.missingFields);

        } else if (error.reason === "INACCESSIBLE_FILES") {
          openModal(<InaccessibleFilesModal {...error.payload} />);
        }
      }
    };

    setIsRefreshing(false);
    mutateWorkflow();

  }, [workflow, source, mutateWorkflow, openModal]);

  function scrollToFit(rowEl: HTMLDivElement) {
    const tableEl = tableRef.current!
    const tableHeight = tableEl.offsetHeight;
    const tableScrollTop = tableEl.scrollTop;

    const rowTop = rowEl.offsetTop - tableEl.offsetTop;
    const rowHeight = rowEl.clientHeight;
    const rowBottom = rowTop + rowHeight;

    if (rowTop < tableScrollTop) {
      tableEl.scroll({ top: rowTop, behavior: "smooth" });

    } else if (rowBottom > tableHeight) {
      const newScrollTop = rowTop - (300 - rowHeight) + 12; // + buffer to show next scrollable row
      tableEl.scroll({ top: newScrollTop, behavior: "smooth" });
    }
  }

  const handleSearchIcon = () => {
    setSourceSearch(prev => ({ ...prev, value: prev.show ? prev.value : '', show: !prev.show }));
  }

  function handleAddChatGPTField() {
    if (!user || (!user.subscription?.featurePortantAi && user?.aiUsage >= 10))
      return openModal(<PayWallModalPortantAI />);

    openModal(<WorkflowChatGPTModal workflow={workflow} mutateWorkflow={mutateWorkflow} />);
  }

  function openModalHubspotFields() {
    openModal(<WorkflowModalHubspotPropertiesList workflow={workflow} refreshWorkflow={mutateWorkflow} onClose={() => { refresh(); }} />);
  }

  const fieldsStatus = (() => {
    if (missingFields === undefined)
      return null;

    if (missingFields.length === 0)
      return (
        <div className="flex flex-col text-sm p-2 border-t">
          <div className="flex items-center">
            <CheckCircleIcon className="w-4 h-4 mr-2 text-green" />{t("All inserted document tags are valid")}
          </div>
        </div>
      );

    return (
      <div className="flex flex-col text-sm p-2 border-t">
        <div className="flex items-center">
          <ExclamationIcon className="w-4 h-4 mr-2 text-yellow" />{t("This document contains invalid tags")} <RefreshIcon className={`h-4 w-4 ml-auto text-blue cursor-pointer ${isRefreshing ? "animate-spin" : "animate-none"}`} onClick={refresh} />
        </div>
        <ul className="list-disc ml-10">
          {missingFields.map(field => <li key={field}>{field}</li>)}
        </ul>
      </div>
    );

  })();

  const rows = useMemo(() => source.sourceFields
    .filter((field: SourceField) => !!field.fieldName && field.fieldType !== "OUTPUT" && field.fieldName.toLowerCase().includes(sourceSearch.value.toLowerCase()) && (source.hubspotConfig?.hubspotProperties?.includes(field.fieldId) || field.fieldId.startsWith("_")))
    .map((field: SourceField) => <WorkflowSourceFieldTableRow key={field.id} field={field} document={props.document} isOpen={openField === field.id} setOpenField={setOpenField} scrollToFit={scrollToFit} workflow={workflow} showUserFieldType disableCopy={props.disableCopy} />)
    , [sourceSearch, openField, props.document, props.disableCopy, workflow, source]);

  const chatGPTRows = source.chatgptFields
    .map((field: ChatGPTField) => <WorkflowChatGPTFieldTableRow key={field.id} field={field} document={props.document} disableCopy={props.disableCopy} />);

  const objectType = source.hubspotConfig?.hubspotObjectTypeName ?? "Contacts";
  const primaryObjectId = source.hubspotConfig?.hubspotObjectTypeId ?? "0-1";

  const showRefreshing = isRefreshing || props.showRefreshSpinning;

  return (
    <div className="flex flex-col border border-gray-400 rounded-sm">
      <div className="border-b border-gray-400">
        <div className="flex items-center text-sm">
          <img className="w-4 h-4 ml-2 my-2" src={BLOCK_ICON_MAP[source.sourceType]} alt="Data Source Icon" />
          <a className="font-gilroy font-semibold ml-1 mt-1 text-blue underline whitespace-nowrap overflow-ellipsis overflow-hidden w-[260px]" href={`https://app.hubspot.com/contacts/${user?.hubspotCredentials?.hubspotPortalId}/objects/${primaryObjectId}`} target="_blank" rel="noreferrer">HubSpot {objectType}</a>
          <SearchIcon className={`h-6 w-6 ml-auto mr-2 text-blue cursor-pointer`} onClick={handleSearchIcon} />
          <CogIcon className={`h-6 w-6 ml-auto mr-2 text-blue cursor-pointer`} onClick={openModalHubspotFields} />
          <RefreshIcon className={`h-6 w-6 mr-2 text-blue cursor-pointer ${showRefreshing ? "animate-spin" : "animate-none"}`} onClick={refresh} />
        </div>
        <div className={`px-2 flex items-center transition-all relative overflow-hidden ${sourceSearch.show ? 'h-12' : 'h-0'}`}>
          {sourceSearch.show &&
            <SmallTextInputField className="w-full" placeholder="Filter fields..." value={sourceSearch.value} onChange={(newValue: string) => setSourceSearch(prev => ({ ...prev, value: newValue }))} autoFocus />
          }
          {sourceSearch.value !== '' && <XIcon className="absolute right-3 top-3 h-6 w-6 text-gray-400 bg-white cursor-pointer" onClick={() => setSourceSearch({ value: '', show: false })} />}
        </div>
      </div>
      {(showRefreshing) &&
        <div className="flex bg-white text-gray-600 items-center py-1 px-2 text-sm border-b last:border-b-0 w-full font-semibold font-gilroy h-[40px]">
          {t("Updating source fields from")}&nbsp;<span className="text-[#f57722]">HubSpot</span><span className="loading-ellipse" />
        </div>
      }
      <div ref={tableRef} className="overflow-y-scroll" style={{ maxHeight: 300 - (showRefreshing ? 40 : 0) }}>
        {rows}
        {(rows.length === 0 && !sourceSearch.show) && (
          <div className="text-xs text-gray-400 text-center p-2">
            {t("No fields found.")}<br />{t("Please ensure the source contains valid fields then refresh the table.")}
          </div>
        )}
        {chatGPTRows}
      </div>
      <div className="overflow-y-scroll border-t" style={{ maxHeight: "210px" }}>
        <div className="flex py-2 px-2 text-sm border-b last:border-b-0 items-center gap-2 text-blue">
          <CogIcon className="w-4 h-4" />
          <div className="flex items-center gap-2 font-semibold font-gilroy cursor-pointer hover:underline" onClick={openModalHubspotFields}>
            Select/Manage Hubspot Properties
          </div>
        </div>
        {!props.hideChatGPTField &&
          <div className="flex py-2 px-2 text-sm border-b last:border-b-0 items-center gap-2 text-blue">
            <PlusCircleIcon className="w-4 h-4" />
            <div className="flex items-center font-semibold font-gilroy cursor-pointer hover:underline mr-auto" onClick={handleAddChatGPTField}>
              Add AI Content Field
            </div>
            <PortantAILabel />
          </div>
        }
      </div>
      {fieldsStatus}
    </div>
  );
};

export default WorkflowSourceFieldTableHubSpot;
